import MyNotesTodayLink from '../features/staffNotes/MyNotesTodayLink';
import React from 'react';
import { Box, ButtonBase, Container, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { usePanelDashboard } from '../hooks/usePanelDashboard';
import { useHomeScreenData } from '../components/Dashboard/shared/useHomeScreenData';
import { getHomeEntryDate } from '../components/Dashboard/shared/homeEntrySelectors';
import { CARE_PROFILE_ROUTE } from '../constants/profileRoutes';
import { CaptureButton, HomeBottomNav, HomeDesktopNav, HomeHeader } from '../components/Home/HomeDashboardChrome';
import {
  HomeCareShortcuts,
  HomeHowItWorks,
  HomeComingUp,
  HomeLibraryShortcuts,
  HomeRecentCare,
  HomeNeedsAttention,
} from '../components/Home/HomeOverviewSections';
import colors from '../assets/theme/colors';
import { findActiveImportHistoryBatch } from '../features/importHistory/importHistoryBatchService';
import { subscribeToAppointments } from '../services/appointmentScheduleService';
import { isUpcomingAppointment } from '../features/appointments/appointmentPresentation';

const HOME_INTRO_DISMISSED_KEY = 'lifelog:home-intro-dismissed';

const HomePage = () => {
  const navigate = useNavigate();
  const hook = usePanelDashboard({ activeChildOnly: true });
  const selectedChildId = hook.currentChildId || '';
  const accessibleChildren = Array.isArray(hook.childrenWithAccess)
    ? hook.childrenWithAccess
    : hook.children;
  const selectedAccessibleChild = selectedChildId
    ? accessibleChildren.find((child) => child.id === selectedChildId) || null
    : null;
  const activeChild = selectedChildId
    ? hook.children.find((child) => child.id === selectedChildId) || null
    : hook.children[0] || null;
  const { todayEntries, recentCareEntries, needsAttention, careSourceCount } = useHomeScreenData(activeChild, hook);
  const childId = activeChild?.id || null;
  const viewerRole = hook?.getUserRoleForChild?.(childId) || null;
  const canManageInboundEmail = ['care_owner', 'care_partner'].includes(viewerRole);
  const introStorageKey = `${HOME_INTRO_DISMISSED_KEY}:${hook.user?.uid || 'anonymous'}`;
  const [showIntro, setShowIntro] = React.useState(() => {
    try { return window.localStorage.getItem(introStorageKey) !== '1'; } catch { return true; }
  });
  const [activeImportBatch, setActiveImportBatch] = React.useState(null);
  const [appointments, setAppointments] = React.useState([]);
  React.useEffect(() => {
    let active = true;
    if (!childId || !canManageInboundEmail) {
      setActiveImportBatch(null);
      return () => { active = false; };
    }
    findActiveImportHistoryBatch({ childId })
      .then((batch) => { if (active) setActiveImportBatch(batch); })
      .catch(() => { if (active) setActiveImportBatch(null); });
    return () => { active = false; };
  }, [canManageInboundEmail, childId]);
  React.useEffect(() => {
    if (!childId) { setAppointments([]); return undefined; }
    return subscribeToAppointments(childId, (items) => {
      setAppointments(items.filter((item) => isUpcomingAppointment(item)).slice(0, 3));
    }, () => setAppointments([]));
  }, [childId]);

  const goToTimeline = () => navigate('/dashboard', {
    state: { selectedChildId: childId, childShellTarget: 'today' },
  });
  const goToCareCircle = () => navigate('/care-team', { state: { childId } });
  const goToHealthReview = (item = null) => navigate(`${CARE_PROFILE_ROUTE}${item?.sourceId ? `?reviewSource=${encodeURIComponent(item.sourceId)}` : ''}#profile-review`, {
    state: { childId, openProfileReview: true },
  });
  const openCapture = () => navigate('/dashboard', {
    state: { selectedChildId: childId, openQuickEntry: true },
  });
  const openImportHistory = () => navigate('/import-history', {
    state: { selectedChildId: childId },
  });
  const openDocuments = () => navigate('/templates?tab=documents', {
    state: { selectedChildId: childId },
  });
  const openMedications = () => navigate(CARE_PROFILE_ROUTE, {
    state: { childId, openChildMedicationManager: { childId } },
  });
  const openAllergies = () => navigate(CARE_PROFILE_ROUTE, {
    state: { childId, openAllergiesManager: true, profileReturnTo: '/home' },
  });
  const openAppointments = (appointment = null) => navigate(`/children/${childId}/appointments${appointment?.id ? `?appointmentId=${encodeURIComponent(appointment.id)}` : ''}`);
  const dismissIntro = () => {
    try { window.localStorage.setItem(introStorageKey, '1'); } catch { /* Keep dismissal for this render. */ }
    setShowIntro(false);
  };
  const openEntryInTimeline = (entry) => {
    if (entry?.kind === 'profile_update_summary') {
      if (entry.profileViewId) {
        navigate(CARE_PROFILE_ROUTE, {
          state: {
            childId,
            profileChapterViewId: entry.profileViewId,
            profileReturnTo: '/home',
          },
        });
        return;
      }
      goToTimeline();
      return;
    }

    if (entry?.kind === 'source_activity_summary') {
      goToTimeline();
      return;
    }

    if (entry?.kind === 'source_receipt' && entry.navigationTarget) {
      navigate(entry.navigationTarget, { state: { childId } });
      return;
    }

    const entryDate = getHomeEntryDate(entry);
    navigate('/dashboard', {
      state: {
        selectedChildId: childId,
        childShellTarget: 'today',
        openCaptureDetailsKey: entry.id,
        timelineEntryId: entry.id,
        selectedDate: entryDate ? entryDate.toISOString() : null,
      },
    });
  };

  if (hook.loading) {
    return <Container maxWidth="sm" sx={{ mt: 6 }}><Typography sx={{ color: colors.semantic.ink2 }}>Loading...</Typography></Container>;
  }

  if (selectedChildId && hook.roleLoading) {
    return <Container maxWidth="sm" sx={{ mt: 6 }}><Typography sx={{ color: colors.semantic.ink2 }}>Switching profiles…</Typography></Container>;
  }

  if (selectedChildId && !selectedAccessibleChild) {
    return (
      <Container maxWidth="sm" sx={{ mt: 6, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '1.1rem', fontWeight: 900, color: colors.semantic.ink, mb: 1 }}>This profile is no longer available.</Typography>
        <Typography sx={{ color: colors.semantic.ink2, mb: 2 }}>Choose another profile to continue.</Typography>
        <ButtonBase
          onClick={() => window.dispatchEvent(new CustomEvent('captureez:open-profile-switcher'))}
          sx={{ px: 2.4, py: 1, borderRadius: '999px', bgcolor: colors.semantic.sageAction, color: colors.semantic.sageActionInk }}
        >
          <Typography sx={{ fontWeight: 900 }}>Choose another profile</Typography>
        </ButtonBase>
      </Container>
    );
  }

  if (selectedChildId && !activeChild) {
    return (
      <Container maxWidth="sm" sx={{ mt: 6, textAlign: 'center' }}>
        <Typography sx={{ color: colors.semantic.ink2 }}>
          {selectedAccessibleChild?.name
            ? `Switching to ${selectedAccessibleChild.name}'s profile…`
            : 'Switching profiles…'}
        </Typography>
      </Container>
    );
  }

  if (!activeChild) {
    return (
      <Container maxWidth="sm" sx={{ mt: 6, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '1.1rem', fontWeight: 900, color: colors.semantic.ink, mb: 1 }}>Add your first profile</Typography>
        <Typography sx={{ color: colors.semantic.ink2, mb: 2 }}>Create a profile for the person you care for to get started.</Typography>
        <ButtonBase onClick={() => hook.handleAddChild?.()} sx={{ px: 2.4, py: 1, borderRadius: '999px', bgcolor: colors.semantic.sageDeep, color: '#FFFFFF' }}>
          <Typography sx={{ fontWeight: 900 }}>Add Profile</Typography>
        </ButtonBase>
      </Container>
    );
  }

  const navigationActions = {
    home: () => {},
    timeline: goToTimeline,
    profile: () => navigate(CARE_PROFILE_ROUTE, { state: { childId } }),
    insights: () => navigate('/dashboard', { state: { selectedChildId: childId, childShellTarget: 'tools' } }),
    careCircle: goToCareCircle,
  };
  const medicationRecords = Array.isArray(activeChild.medicationRecords)
    ? activeChild.medicationRecords
    : Array.isArray(activeChild.medicationDetails) ? activeChild.medicationDetails : null;
  const medicationCount = medicationRecords
    ? medicationRecords.filter((medication) => medication?.name && !medication?.isArchived).length
    : null;
  const allergyRecords = Array.isArray(activeChild.medicalProfile?.foodAllergies)
    ? activeChild.medicalProfile.foodAllergies
    : (Array.isArray(activeChild.foodAllergies) ? activeChild.foodAllergies : []);
  const careCircleCount = Number.isFinite(activeChild.careTeamMemberCount) ? activeChild.careTeamMemberCount : null;
  const recentCandidates = [...todayEntries, ...recentCareEntries]
    .filter((entry, index, entries) => entries.findIndex((candidate) => candidate.id === entry.id) === index)
  const meaningfulRecentCare = recentCandidates.filter((entry) => !['source_activity_summary', 'profile_update_summary'].includes(entry.kind));
  const recentCare = (meaningfulRecentCare.length ? meaningfulRecentCare : recentCandidates).slice(0, 3);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: colors.semantic.profileFlow.careStory.pageBg, pb: { xs: '88px', md: 5 } }}>
      <Container maxWidth="lg" sx={{ py: { xs: 2.2, sm: 3, md: 4 } }}>
        <Box sx={{ maxWidth: 680, mx: 'auto' }}>
          <HomeHeader activeChild={activeChild} user={hook.user} />
          <MyNotesTodayLink child={activeChild} userId={hook.user?.uid} userRole={viewerRole} />
          <HomeDesktopNav actions={navigationActions} />
          <Box sx={{ mt: { xs: 2, sm: 2.5 }, textAlign: 'center' }}>
            <CaptureButton onClick={openCapture} />
            <Typography sx={{ mt: 1, fontSize: '0.86rem', color: colors.semantic.ink2 }}>Anything you capture gets filed automatically.</Typography>
          </Box>
          {showIntro ? <HomeHowItWorks onDismiss={dismissIntro} /> : null}
          <HomeNeedsAttention needsAttention={needsAttention} onReview={goToHealthReview} />
          <HomeComingUp appointments={appointments} onOpenAppointment={openAppointments} onViewAll={openAppointments} />
          <HomeRecentCare entries={recentCare} onOpenEntry={openEntryInTimeline} onViewAll={goToTimeline} />
          <HomeCareShortcuts medicationCount={medicationCount} allergyCount={allergyRecords.length} allergyRecords={allergyRecords} careCircleCount={careCircleCount} onMedications={openMedications} onAllergies={openAllergies} onCareCircle={goToCareCircle} onAppointments={openAppointments} />
          <HomeLibraryShortcuts documentCount={Number.isFinite(careSourceCount) ? careSourceCount : null} importDescription={activeImportBatch ? 'Open your unfinished history import.' : ''} onDocuments={openDocuments} onImport={canManageInboundEmail ? openImportHistory : null} />
        </Box>
      </Container>
      <HomeBottomNav actions={navigationActions} />
    </Box>
  );
};

export default HomePage;
